import './VideoUploader.pcss';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import Spinner from '../../../../../../common/components/Spinner';
import { uploadVideoWithUrl } from '@mm/state/actions/uploadActions';
import { StoreState } from '@mm/state/store-interfaces';
import {
  VIDEO_EXTENSIONS,
  MAX_VIDEO_SIZE,
} from '../../../../../../common/widgets/VideoWidget/constants';
import modalActions from '@mm/state/modules/modalsState/actions';

const useUploadVideo = () => {
  const [isUploading, setIsUploading] = useState(false);
  const dispatch: Dispatch<StoreState> = useDispatch();

  const upload = async (
    [file]: File[],
    rejectedFiles: File[],
    onUpload: (assetId: number, url: string) => void,
  ) => {
    if (rejectedFiles.length) {
      return dispatch(
        modalActions.openPrettyErrorModal({
          title: 'Unsupported Extension',
          errorMessage: `Supported file extensions are .mov and .mp4`,
        }),
      );
    }

    if (file) {
      if (file.size > MAX_VIDEO_SIZE) {
        return dispatch(
          modalActions.openPrettyErrorModal({
            title: 'Upload Size Limit',
            errorMessage: 'The video should be less than 25MB',
          }),
        );
      }

      try {
        setIsUploading(true);
        const [id, url] = await dispatch(
          uploadVideoWithUrl({
            content: file,
            extension: file.name.split('.').pop() || '',
          }),
        );
        onUpload(id, url);
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
      }
    }
  };

  return { isUploading, upload };
};

type Props = {
  onUpload: (assetId: number, url: string) => void;
  className?: string;
  file: string | null;
  disabled?: boolean;
  description: string;
};

const VideoUploader = (props: Props) => {
  const { className, onUpload, file, description, disabled } = props;
  const { upload, isUploading } = useUploadVideo();
  const { getRootProps, getInputProps } = useDropzone({
    accept: VIDEO_EXTENSIONS,
    disabled,
    onDrop: (acceptedFiles, rejectedFiles) =>
      upload(acceptedFiles, rejectedFiles, onUpload),
  });

  return (
    <div
      className={classnames('video-uploader', className, {
        'video-uploader--has-file': file,
      })}
    >
      {file ? (
        <div className="video-uploader__file-area-wrapper">
          {isUploading && (
            <div className="video-uploader__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <video
            title={description}
            className="video-uploader__video"
            controls
            src={file}
          />

          <div
            className="video-uploader__file-area-upload-hover"
            {...getRootProps()}
          >
            <input {...getInputProps()} accept="video/mp4,video/quicktime" />

            <i className="far fa-file-video fa-3x video-uploader__upload-icon" />
            <span>Upload new Video</span>

            <div className="video-uploader__file-area-upload-hover-blinder" />
          </div>
        </div>
      ) : (
        <div className="video-uploader__drop-area" {...getRootProps()}>
          {isUploading && (
            <div className="video-uploader__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <input {...getInputProps()} accept="video/mp4,video/quicktime" />

          <div className="video-uploader__drop-area-text">
            <i className="far fa-file-video fa-3x video-uploader__upload-icon" />
            <span>
              Drop video file or
              <span className="video-uploader__choose-file">choose file</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(VideoUploader);
